import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Modal,
  PageContainer,
  Skeleton,
  TextArea,
  useToast,
} from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
// Online orders carry their OWN currency, not the tenant's — an order may be
// placed on a storefront in a different one, so this must NOT use useTenantCurrency().
import { toCurrencyCode } from '@/hooks/use-tenant-currency';
import { siteOrderService } from '@/services/site-order.service';
import {
  amendmentSummary,
  amendmentsFromShortfall,
  availableActions,
  statusTone,
} from '@/lib/site-order-view';
import { ApiError } from '@/lib/api-client';
import type { SiteOrderDetail } from '@/types/site-order';

/**
 * /online-orders/:id — one order, and everything the shop can do with it.
 *
 * The amendment control matters more than it looks. Somebody who cannot supply
 * all of something must be able to say so IN the acceptance, because the
 * alternative — accept, then edit the bill — leaves the customer's own status
 * page describing an order that is not the one being packed. Quantities go down
 * only; the server refuses an increase, because raising one means collecting
 * more money from a customer who is not present to agree.
 */

const SHORT_REASON_LABEL: Record<string, string> = {
  OUT_OF_STOCK: 'Out of stock',
  DISCONTINUED: 'No longer sold',
  PRICE_CHANGED: 'Price changed',
  OTHER: 'Unavailable',
};

export function OnlineOrderDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [order, setOrder] = useState<SiteOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Line id → what the shop will actually supply. Empty means "all of it". */
  const [amendments, setAmendments] = useState<Record<string, number>>({});
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOrder(await siteOrderService.get(id));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      toast({ title: done, variant: 'success' });
      await load();
      setAmendments({});
    } catch (err) {
      /*
       * A stock conflict is not merely an error — it is a list of exactly what
       * to change. The 409 enumerates every short line with what is actually
       * available, so pre-filling the amendments turns "not enough stock" into
       * one click.
       */
      if (err instanceof ApiError && err.statusCode === 409 && order) {
        const prefill = amendmentsFromShortfall(
          err.details as Array<{ field: string; message: string; available?: number }>,
          order.items,
        );
        if (Object.keys(prefill).length > 0) {
          setAmendments((current) => ({ ...current, ...prefill }));
          setError(`${err.message} — the quantities below have been reduced to what you have.`);
          return;
        }
      }
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !order) {
    return (
      <PageContainer title="Online order">
        <Skeleton className="h-64 w-full rounded-xl" />
      </PageContainer>
    );
  }

  if (!order) {
    return (
      <PageContainer title="Online order">
        <Alert variant="danger" description={error ?? 'Order not found'} />
      </PageContainer>
    );
  }

  const status = statusTone(order.status);
  const can = availableActions(order);
  const summary = amendmentSummary(order.items, amendments);
  const money = order.grand_total !== null;
  const price = (paise: number) => formatCurrency(paise, toCurrencyCode(order.currency));

  return (
    <PageContainer
      title={order.order_number}
      subtitle={`${order.customer_name} · ${order.fulfilment_mode.toLowerCase()}`}
      actions={
        <Button variant="secondary" size="sm" onClick={() => navigate('/online-orders')}>
          Back to list
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge variant={status.tone}>{status.label}</Badge>
        {order.payment_status === 'PAID' && <Badge variant="success">Paid online</Badge>}
        {order.bill_id && <Badge variant="parked">Draft bill created</Badge>}
      </div>

      {error && <Alert variant="danger" description={error} className="mb-4" />}
      {order.reject_reason && (
        <Alert variant="warning" title="Rejected" description={order.reject_reason} className="mb-4" />
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <section className="rounded-xl border border-neutral-100 bg-white p-5 shadow-md dark:border-neutral-700 dark:bg-neutral-800">
          <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400">Customer</h2>
          <p className="mt-1 font-medium text-neutral-900 dark:text-white">{order.customer_name}</p>
          {/* Guest checkout is unverified by design, so this number is the
              shop's only handle on the person who placed the order. */}
          <a href={`tel:${order.customer_phone}`} className="text-sm text-brand-600 underline">
            {order.customer_phone}
          </a>
          {order.customer_email && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{order.customer_email}</p>
          )}
          <p className="mt-2 text-xs text-neutral-400">
            Placed {new Date(order.created_at).toLocaleString()}
          </p>
          {order.customer_note && (
            <p className="mt-3 rounded-lg bg-brand-50 p-3 text-sm text-brand-900 dark:bg-neutral-900 dark:text-brand-200">
              “{order.customer_note}”
            </p>
          )}
          {order.requested_slot && (
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
              Wants it: {order.requested_slot}
            </p>
          )}
          {order.delivery && (
            <address className="mt-3 rounded-lg bg-teal-50 p-3 text-sm not-italic text-neutral-800 dark:bg-neutral-900 dark:text-neutral-200">
              {order.delivery.address_line}
              {order.delivery.landmark && <>, {order.delivery.landmark}</>}
              {order.delivery.city && <>, {order.delivery.city}</>}
              {order.delivery.pincode && <> {order.delivery.pincode}</>}
            </address>
          )}
        </section>

        <section className="rounded-xl border border-neutral-100 bg-white p-5 shadow-md lg:col-span-2 dark:border-neutral-700 dark:bg-neutral-800">
          <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400">
            {can.accept ? 'Reduce anything you are short of' : 'Items'}
          </h2>
          <table className="mt-3 w-full text-sm">
            <tbody>
              {order.items.map((item) => {
                const supplied = amendments[item.id] ?? item.quantity;
                const settled = item.accepted_quantity;
                return (
                  <tr
                    key={item.id}
                    className="border-b border-neutral-100 last:border-0 dark:border-neutral-700"
                  >
                    <td className="py-3">
                      <p className="font-medium text-neutral-900 dark:text-white">
                        {item.product_name}
                        {item.variant_label ? ` — ${item.variant_label}` : ''}
                      </p>
                      <p className="text-xs text-neutral-400">{item.product_sku}</p>
                      {settled !== null && settled < item.quantity && (
                        <p className="text-xs text-warning-700 dark:text-warning-400">
                          Supplied {settled} of {item.quantity}
                          {item.short_reason
                            ? ` · ${SHORT_REASON_LABEL[item.short_reason] ?? item.short_reason}`
                            : ''}
                        </p>
                      )}
                    </td>
                    <td className="py-3 text-right">
                      {can.accept ? (
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            aria-label={`Reduce ${item.product_name}`}
                            disabled={supplied <= 0}
                            onClick={() =>
                              setAmendments((a) => ({ ...a, [item.id]: Math.max(0, supplied - 1) }))
                            }
                            className="h-8 w-8 rounded-lg border border-neutral-200 transition-all duration-200 hover:bg-neutral-50 disabled:opacity-40 dark:border-neutral-600"
                          >
                            −
                          </button>
                          <span className="min-w-12 text-center tabular-nums">
                            {supplied}
                            {supplied < item.quantity && (
                              <span className="text-neutral-400"> / {item.quantity}</span>
                            )}
                          </span>
                          <button
                            type="button"
                            aria-label={`Increase ${item.product_name}`}
                            // Capped at what was ordered: the server refuses
                            // more, so a button that could ask would only
                            // produce a confusing failure.
                            disabled={supplied >= item.quantity}
                            onClick={() =>
                              setAmendments((a) => ({
                                ...a,
                                [item.id]: Math.min(item.quantity, supplied + 1),
                              }))
                            }
                            className="h-8 w-8 rounded-lg border border-neutral-200 transition-all duration-200 hover:bg-neutral-50 disabled:opacity-40 dark:border-neutral-600"
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <span className="tabular-nums text-neutral-600 dark:text-neutral-300">
                          {settled ?? item.quantity} {item.unit ?? ''}
                        </span>
                      )}
                    </td>
                    <td className="w-28 py-3 text-right tabular-nums text-neutral-900 dark:text-white">
                      {money && item.line_total !== null ? price(item.line_total) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {money && (
            <dl className="mt-4 space-y-1 border-t border-neutral-200 pt-3 text-sm dark:border-neutral-700">
              {order.delivery_fee !== null && order.delivery_fee > 0 && (
                <div className="flex justify-between">
                  <dt className="text-neutral-500">Delivery</dt>
                  <dd className="tabular-nums">{price(order.delivery_fee)}</dd>
                </div>
              )}
              <div className="flex justify-between text-base font-semibold">
                <dt>Order total</dt>
                <dd className="tabular-nums">{price(order.grand_total as number)}</dd>
              </div>
              {summary.changed && (
                // Deliberately no recomputed figure. Both the storefront and
                // the till take their totals from the server; guessing one here
                // is how a customer ends up quoted two different numbers.
                <p className="pt-1 text-xs text-warning-700 dark:text-warning-400">
                  The bill will be recalculated for what you supply.
                </p>
              )}
            </dl>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {can.accept && (
              <Button
                variant="primary"
                disabled={busy || summary.supplied === 0}
                onClick={() =>
                  void act(
                    () =>
                      siteOrderService.accept(
                        order.id,
                        summary.changed
                          ? order.items.map((i) => ({
                              item_id: i.id,
                              accepted_quantity: amendments[i.id] ?? i.quantity,
                            }))
                          : undefined,
                      ),
                    'Order accepted — a draft bill is ready',
                  )
                }
              >
                {/* The button says what it is about to do, so nobody shorts a
                    customer without reading it first. */}
                {summary.supplied === 0
                  ? 'Nothing to supply'
                  : summary.changed
                    ? `Accept ${summary.supplied} of ${summary.ordered}`
                    : 'Accept order'}
              </Button>
            )}
            {can.ready && (
              <Button
                variant="success"
                disabled={busy}
                onClick={() => void act(() => siteOrderService.markReady(order.id), 'Marked ready')}
              >
                Mark ready
              </Button>
            )}
            {can.contact && (
              <Button
                variant="primary"
                disabled={busy}
                onClick={() =>
                  void act(() => siteOrderService.markContacted(order.id), 'Marked contacted')
                }
              >
                Mark contacted
              </Button>
            )}
            {can.close && (
              <>
                <Button
                  variant="success"
                  disabled={busy}
                  onClick={() =>
                    void act(() => siteOrderService.closeEnquiry(order.id, true), 'Closed as won')
                  }
                >
                  Won
                </Button>
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() =>
                    void act(() => siteOrderService.closeEnquiry(order.id, false), 'Closed as lost')
                  }
                >
                  Lost
                </Button>
              </>
            )}
            {can.reject && (
              <Button variant="danger" disabled={busy} onClick={() => setRejecting(true)}>
                Reject
              </Button>
            )}
            {/* Completion happens at the till, where the money is taken. There
                is deliberately no second payment screen here. */}
            {can.complete && (
              <p className="w-full text-sm text-neutral-500 dark:text-neutral-400">
                Take payment on the POS billing screen — this order is draft bill{' '}
                <span className="font-mono text-xs">{order.bill_id}</span>.
              </p>
            )}
          </div>
        </section>
      </div>

      <Modal
        open={rejecting}
        onClose={() => setRejecting(false)}
        title={`Reject ${order.order_number}?`}
      >
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          {/* Shown to the customer on their own status page. "Rejected" with no
              explanation is the message they show their friends. */}
          The customer sees this reason, so say something they can act on.
        </p>
        <TextArea
          value={rejectReason}
          onChange={setRejectReason}
          rows={3}
          maxLength={300}
          placeholder="Sorry — we are out of these until Thursday."
          className="mt-3"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setRejecting(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={busy || rejectReason.trim().length < 3}
            onClick={() =>
              void act(async () => {
                await siteOrderService.reject(order.id, rejectReason.trim());
                setRejecting(false);
                setRejectReason('');
              }, 'Order rejected')
            }
          >
            Reject order
          </Button>
        </div>
      </Modal>
    </PageContainer>
  );
}
